import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Loader2, Phone, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SlotPicker } from '../../components/booking/SlotPicker';
import { BookingSummary, BookingShell } from '../../components/booking/BookingShell';
import { detectTimezone, fmtDate, fmtTime, tzLabel } from '../../lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Returns an error message, or null when the form is valid.
export const validateBooking = ({ name, email, phone }) => {
  if (!name.trim()) return 'Please enter your name.';
  if (!email.trim()) return 'Email address is required.';
  if (!EMAIL_RE.test(email.trim())) return 'Please enter a valid email address.';
  if (!phone.trim()) return 'Phone number is required.';
  let digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return 'Please enter a valid 10-digit US phone number.';
  return null;
};

const Field = ({ label, children }) => (
  <label className="block space-y-1">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {children}
  </label>
);

const req = <span className="text-red-600" aria-hidden="true">*</span>;

const BookingForm = ({ form, setForm, onBack, onSubmit, submitting, error }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form className="space-y-3" noValidate onSubmit={(e) => { e.preventDefault(); onSubmit(); }} data-testid="booking-form">
      <Field label={<>Full name {req}</>}><Input value={form.name} onChange={set('name')} required aria-required="true" data-testid="booking-name" /></Field>
      <Field label={<>Email {req}</>}><Input type="email" value={form.email} onChange={set('email')} required aria-required="true" placeholder="you@example.com" data-testid="booking-email" /></Field>
      <Field label={<>Phone {req}</>}><Input type="tel" value={form.phone} onChange={set('phone')} required aria-required="true" placeholder="(555) 123-4567" data-testid="booking-phone" /></Field>
      <Field label="Note (optional)">
        <textarea rows={3} placeholder="Anything you'd like to share?" value={form.note} onChange={set('note')}
          className="w-full rounded-md border border-input px-3 py-2 text-sm resize-y" data-testid="booking-note" />
      </Field>
      {error && <p className="text-sm text-red-600 flex items-center gap-1" role="alert" data-testid="booking-error"><AlertCircle size={14} /> {error}</p>}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack} data-testid="booking-back">Back</Button>
        <Button type="submit" disabled={submitting} className="bg-[#1a3a5c] hover:bg-[#14304d] flex-1" data-testid="booking-submit">
          {submitting ? <Loader2 className="animate-spin" size={16} /> : 'Confirm booking'}
        </Button>
      </div>
    </form>
  );
};

export default function BookingPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [slots, setSlots] = useState([]);
  const [tz, setTz] = useState(detectTimezone());
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [booked, setBooked] = useState(null);

  const loadSlots = () => axios.get(`${API}/public/book/${slug}/slots`).then((r) => setSlots(r.data.slots));
  useEffect(() => {
    axios.get(`${API}/public/book/${slug}`).then((r) => { setInfo(r.data); return loadSlots(); })
      .catch((e) => setError(e.response?.data?.detail || 'This booking link is not available.'));
  }, [slug]); // eslint-disable-line

  const submit = async () => {
    const invalid = validateBooking(form);
    if (invalid) { setFormError(invalid); return; }
    setSubmitting(true); setFormError(null);
    try {
      const r = await axios.post(`${API}/public/book/${slug}`, { ...form, starts_at: selected.starts_at, timezone: tz });
      setBooked(r.data);
    } catch (e) {
      setFormError(e.response?.data?.detail || 'Booking failed. Please try again.');
      if (e.response?.status === 409) { setSelected(null); loadSlots(); }
    } finally { setSubmitting(false); }
  };

  if (error) return <BookingShell><p className="text-center text-slate-600 py-10" data-testid="booking-unavailable">{error}</p></BookingShell>;
  if (!info) return <BookingShell><div className="py-16 text-center"><Loader2 className="animate-spin inline" /></div></BookingShell>;
  if (booked) return <BookingShell><BookingSummary booking={booked} tz={tz} heading="You're booked!" /></BookingShell>;

  return (
    <BookingShell>
      <div className="grid md:grid-cols-[260px_1fr] gap-8">
        <aside className="space-y-3 md:border-r md:pr-6">
          <p className="text-sm text-slate-500">{info.name}</p>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="booking-title">{info.title}</h1>
          <p className="text-sm text-slate-600 flex items-center gap-2"><Clock size={15} /> {info.duration_minutes} minutes</p>
          {info.meet && <p className="text-sm text-slate-600 flex items-center gap-2"><Phone size={15} /> Phone Call</p>}
          {selected && (
            <p className="text-sm text-emerald-700 flex items-start gap-2 pt-2" data-testid="booking-selected">
              <Calendar size={15} className="mt-0.5" />
              <span>{fmtDate(selected.starts_at, tz)}<br />{fmtTime(selected.starts_at, tz)} – {fmtTime(selected.ends_at, tz)} {tzLabel(tz, new Date(selected.starts_at))}</span>
            </p>
          )}
        </aside>
        <section>
          {!selected ? (
            <>
              <h2 className="text-base md:text-lg font-semibold mb-3">Pick a time</h2>
              <SlotPicker slots={slots} tz={tz} onTzChange={setTz} selected={selected} onSelect={setSelected} />
            </>
          ) : (
            <>
              <h2 className="text-base md:text-lg font-semibold mb-3">Your details</h2>
              <BookingForm form={form} setForm={setForm} onBack={() => setSelected(null)} onSubmit={submit} submitting={submitting} error={formError} />
            </>
          )}
        </section>
      </div>
    </BookingShell>
  );
}
